import { useState, useEffect, useRef } from "react";
import { useSelector } from 'react-redux';
import * as XLSX from "xlsx";
import MaterialTable from 'material-table';
import { Upload, Download, AlertCircle, CheckCircle, X, Trash2 } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Fixed POPC fields — order matches the lender list
const POPC_FIELDS = [
  { field: "HDFCLoans_POPC",      title: "HDFC Loans POPC"      },
  { field: "HDFCCards_POPC",      title: "HDFC Cards POPC"      },
  { field: "Chola_POPC",          title: "Chola POPC"           },
  { field: "IDFC_POPC",           title: "IDFC POPC"            },
  { field: "INCRED_POPC",         title: "INCRED POPC"          },
  { field: "AakashaFinance_POPC", title: "Aakasha Finance POPC" },
  { field: "TATA_CAPITAL_POPC",   title: "TATA Capital POPC"    },
  { field: "BIZ_LOAN_POPC",       title: "Biz Loan POPC"        },
  { field: "SK_Finance_POPC",     title: "S.K Finance POPC"     },
  { field: "SURYODAY_POPC",       title: "SURYODAY POPC"        },
  { field: "PIRAMAL_POPC",        title: "PIRAMAL POPC"         },
  { field: "TOYOTA_POPC",         title: "TOYOTA POPC"          },
  { field: "BMW_Financial_POPC",  title: "BMW Financial POPC"   },
  { field: "ESAF_POPC",           title: "ESAF POPC"            },
  { field: "Kotak_POPC",          title: "Kotak POPC"           },
];

const FIXED_COLUMNS = [
  { title: "Company",            field: "company" },
  { title: "Employee ID",        field: "employeeId" },
  { title: "FE Name",            field: "feName" },
  { title: "PAN",                field: "pan" },
  { title: "Bank Name",          field: "bankName" },
  { title: "Bank Type",          field: "bankType" },
  { title: "Category",           field: "category" },
  { title: "Account No.",        field: "accountNo" },
  { title: "IFSC Code",          field: "ifscCode" },
  { title: "Remarks for Account",field: "remarksForAccount" },
  { title: "Main Portfolio",     field: "mainPortfolio" },
  { title: "Zone",               field: "zone" },
  { title: "State",              field: "state" },
  { title: "Department",         field: "department" },
  { title: "Retainership",       field: "retainership",        type: "numeric" },
  { title: "No of Days on Field",field: "daysOnField",         type: "numeric" },
  { title: "Reimbursement/Day",  field: "reimbursementPerDay", type: "numeric" },
  { title: "Total Reimbursement",field: "totalReimbursement",  type: "numeric" },
  { title: "TDS",                field: "tds",                 type: "numeric" },
  { title: "TDS Amount",         field: "tdsAmount",           type: "numeric" },
];

// Maps normalised Excel header → model field key
const HEADER_MAP = {
  "employee id":             "employeeId",
  "employee_id":             "employeeId",
  "fe name":                 "feName",
  "pan":                     "pan",
  "bank name":               "bankName",
  "account no.":             "accountNo",
  "account no":              "accountNo",
  "ifsc code":               "ifscCode",
  "remarks for account":     "remarksForAccount",
  "bank type":               "bankType",
  "banktype":                "bankType",
  "category":                "category",
  "main portfolio":          "mainPortfolio",
  "zone":                    "zone",
  "state":                   "state",
  "department":              "department",
  "retainership":            "retainership",
  "no of days on field":     "daysOnField",
  "no. of days on field":    "daysOnField",
  "reimbursement per day":   "reimbursementPerDay",
  "reimbursement/day":       "reimbursementPerDay",
  "total reimbursement":     "totalReimbursement",
  "tds":                     "tds",
  "tds%":                    "tds",
  "tds amount":              "tdsAmount",
  "tdsamount":               "tdsAmount",
  // Fixed POPC fields
  "hdfcloans- popc":         "HDFCLoans_POPC",
  "hdfcloans popc":          "HDFCLoans_POPC",
  "hdfccards- popc":         "HDFCCards_POPC",
  "hdfccards popc":          "HDFCCards_POPC",
  "chola- popc":             "Chola_POPC",
  "chola popc":              "Chola_POPC",
  "idfc- popc":              "IDFC_POPC",
  "idfc popc":               "IDFC_POPC",
  "incred- popc":            "INCRED_POPC",
  "incred popc":             "INCRED_POPC",
  "aakasha finance popc":    "AakashaFinance_POPC",
  "aakasha finance- popc":   "AakashaFinance_POPC",
  "tata capital popc":       "TATA_CAPITAL_POPC",
  "tata capital- popc":      "TATA_CAPITAL_POPC",
  "biz loan popc":           "BIZ_LOAN_POPC",
  "biz loan- popc":          "BIZ_LOAN_POPC",
  "s.k finance  popc":       "SK_Finance_POPC",
  "s.k finance popc":        "SK_Finance_POPC",
  "s.k finance- popc":       "SK_Finance_POPC",
  "sk finance popc":         "SK_Finance_POPC",
  "suryoday- popc":          "SURYODAY_POPC",
  "suryoday popc":           "SURYODAY_POPC",
  "piramal- popc":           "PIRAMAL_POPC",
  "piramal popc":            "PIRAMAL_POPC",
  "toyota- popc":            "TOYOTA_POPC",
  "toyota popc":             "TOYOTA_POPC",
  "bmw financial- popc":     "BMW_Financial_POPC",
  "bmw financial popc":      "BMW_Financial_POPC",
  "esaf- popc":              "ESAF_POPC",
  "esaf popc":               "ESAF_POPC",
  "kotak- popc":             "Kotak_POPC",
  "kotak popc":              "Kotak_POPC",
};

const TEMPLATE_HEADERS = [
  "Employee ID", "FE Name", "PAN", "BANK NAME", "ACCOUNT NO.", "IFSC CODE",
  "Remarks for Account", "Bank Type", "Category", "Main Portfolio", "Zone", "State",
  "Department", "Retainership", "No of days on field",
  "Reimbursement per day", "Total Reimbursement", "TDS", "TDS Amount",
  "HDFCLoans- POPC", "HDFCCards- POPC",
  "Chola- POPC", "IDFC- POPC", "INCRED- POPC",
  "Aakasha Finance POPC", "TATA CAPITAL POPC", "BIZ LOAN POPC",
  "S.K Finance  POPC", "SURYODAY- POPC", "PIRAMAL- POPC",
  "TOYOTA- POPC", "BMW Financial- POPC", "ESAF- POPC", "Kotak- POPC",
];

const PriceMaster = () => {
  const role = useSelector((state) => state.getRoleInfo?.getRoleData || {});
  const [records, setRecords]               = useState([]);
  const [selectedRows, setSelectedRows]     = useState([]);
  const [loading, setLoading]               = useState(false);
  const [isImportOpen, setIsImportOpen]     = useState(false);
  const [importFile, setImportFile]         = useState(null);
  const [importLoading, setImportLoading]   = useState(false);
  const [importErrors, setImportErrors]     = useState([]);
  const [importSuccess, setImportSuccess]   = useState(0);
  const [companies, setCompanies]           = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const fileInputRef = useRef(null);

  const fetchCompanies = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfo"));
      const res = await fetch(`${API_URL}/company?isActive=true&limit=1000`, {
        headers: { Authorization: `Bearer ${userInfo?.token}` },
      });
      const data = await res.json();
      if (data.companies) setCompanies(data.companies);
    } catch (e) {
      console.error("Failed to fetch companies:", e);
    }
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/price-master`);
      if (!res.ok) { console.error("Failed to fetch price master:", res.status); return; }
      const data = await res.json();
      if (data.success) setRecords(data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecords(); fetchCompanies(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await fetch(`${API_URL}/price-master/${id}`, { method: "DELETE" });
      setRecords((prev) => prev.filter((r) => r._id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Delete ${selectedRows.length} selected record${selectedRows.length > 1 ? "s" : ""}?`)) return;
    try {
      const ids = selectedRows.map((r) => r._id);
      await fetch(`${API_URL}/price-master/bulk`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      setRecords((prev) => prev.filter((r) => !ids.includes(r._id)));
      setSelectedRows([]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Delete ALL records? This cannot be undone.")) return;
    try {
      await fetch(`${API_URL}/price-master/all`, { method: "DELETE" });
      setRecords([]);
      setSelectedRows([]);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS]);
    ws["!cols"] = TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(h.length + 4, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Price Master");
    XLSX.writeFile(wb, "price_master_template.xlsx");
  };

  const handleImport = async () => {
    if (!importFile) { alert("Please choose a file."); return; }
    if (!selectedCompany) { alert("Please select a company."); return; }
    setImportLoading(true);
    setImportErrors([]);
    setImportSuccess(0);

    try {
      const arrayBuffer = await importFile.arrayBuffer();
      const wb = XLSX.read(arrayBuffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const errors = [];
      const batch  = [];

      rows.forEach((row, i) => {
        const rowNum = i + 2;
        const record = {};
        const extra  = {};

        Object.entries(row).forEach(([key, val]) => {
          const normalized = key.trim().toLowerCase();
          const mapped = HEADER_MAP[normalized];
          if (mapped) {
            const numericFields = [
              "retainership","daysOnField","reimbursementPerDay","totalReimbursement","tds","tdsAmount",
              ...POPC_FIELDS.map((p) => p.field),
            ];
            record[mapped] = numericFields.includes(mapped) ? (Number(val) || 0) : val;
          } else if (key.trim()) {
            extra[key.trim()] = val;
          }
        });

        if (Object.keys(extra).length > 0) record.extraFields = extra;

        if (!record.feName) {
          errors.push(`Row ${rowNum}: 'FE Name' is required`);
          return;
        }
        record.company = selectedCompany;
        record.tdsAmount = +((record.totalReimbursement || 0) * ((record.tds || 0) / 100)).toFixed(2);
        batch.push(record);
      });

      if (batch.length > 0) {
        const res = await fetch(`${API_URL}/price-master/bulk`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(batch),
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch {
          errors.push(`Server error (${res.status}): unexpected response from server`);
          setImportErrors(errors);
          setImportLoading(false);
          return;
        }
        if (data.success) {
          setImportSuccess(data.count);
          fetchRecords();
        } else {
          errors.push(`Server error: ${data.message}`);
        }
      }

      setImportErrors(errors);
    } catch (e) {
      setImportErrors([`Failed to parse file: ${e.message}`]);
    } finally {
      setImportLoading(false);
    }
  };

  const closeImport = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportErrors([]);
    setImportSuccess(0);
    setSelectedCompany('');
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const cs = { fontSize: 12 };
  const hs = { fontSize: 12, fontWeight: "bold" };
  const fmtNum = (val) => (val != null && val !== '') ? Number(val).toFixed(2) : '0.00';

  const companyMap = Object.fromEntries(companies.map((c) => [c._id, c.companyName]));

  const columns = [
    ...FIXED_COLUMNS.map((col) => ({
      ...col,
      cellStyle: cs,
      headerStyle: hs,
      ...(col.field === "company"
        ? { render: (row) => companyMap[row.company] || row.company || "—" }
        : col.type === "numeric"
        ? { render: (row) => fmtNum(row[col.field]) }
        : {}),
    })),
    ...POPC_FIELDS.map(({ field, title }) => ({
      title,
      field,
      type: "numeric",
      cellStyle: cs,
      headerStyle: hs,
      render: (row) => fmtNum(row[field]),
    })),
    {
      title: "Actions",
      field: "actions",
      sorting: false,
      cellStyle: cs,
      render: (rowData) => role.priceMasterDelete !== false ? (
        <button
          onClick={() => handleDelete(rowData._id)}
          className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
          title="Delete"
        >
          <Trash2 size={15} />
        </button>
      ) : null,
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="bg-white shadow-lg rounded-xl p-6 border border-gray-100">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Price Master</h2>
          <div className="flex items-center gap-3">
            {selectedRows.length > 0 && role.priceMasterDelete !== false && (
              <button
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete Selected ({selectedRows.length})
              </button>
            )}
            {records.length > 0 && selectedRows.length === 0 && role.priceMasterDelete !== false && (
              <button
                onClick={handleDeleteAll}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete All
              </button>
            )}
            {role.priceMasterImport !== false && (
              <button
                onClick={() => setIsImportOpen(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition flex items-center gap-2"
              >
                <Upload size={18} />
                Import
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <MaterialTable
            title=""
            columns={columns}
            data={records}
            isLoading={loading}
            onSelectionChange={(rows) => setSelectedRows(rows)}
            options={{
              search: true,
              paging: true,
              pageSize: 15,
              pageSizeOptions: [15, 30, 50],
              emptyRowsWhenPaging: false,
              exportButton: false,
              toolbar: true,
              showTitle: false,
              columnsButton: false,
              tableLayout: "auto",
              selection: true,
              headerStyle: {
                fontWeight: "bold",
                fontSize: 12,
                backgroundColor: "#f9fafb",
                whiteSpace: "nowrap",
              },
              rowStyle: { height: "48px", fontSize: 12 },
              cellStyle: { whiteSpace: "nowrap" },
            }}
          />
        </div>
      </div>

      {/* Import Modal */}
      {isImportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Upload size={18} className="text-green-600" />
                Import Price Master
              </h3>
              <button onClick={closeImport} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Template download */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <div>
                  <p className="text-sm font-medium text-gray-700">Excel Template</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Employee ID · FE Name · PAN · BANK NAME · ACCOUNT NO. · IFSC CODE · Remarks · Main Portfolio · Zone · State · Department · Retainership · Days on Field · Reimbursement/Day · Total Reimbursement
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    <span className="font-medium text-gray-500">POPC columns:</span>{" "}
                    HDFCLoans · HDFC_HLLoans · HDFCCards · Chola · IDFC · INCRED · Aakasha Finance · TATA CAPITAL · BIZ LOAN · S.K Finance · SURYODAY · PIRAMAL · TOYOTA · BMW Financial · ESAF · Kotak
                  </p>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium py-1.5 px-3 rounded-lg hover:bg-gray-100 transition"
                >
                  <Download size={15} />
                  Download
                </button>
              </div>

              {/* Company selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">-- Select Company --</option>
                  {companies.map((c) => (
                    <option key={c._id} value={c._id}>{c.companyName}</option>
                  ))}
                </select>
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Filled File <span className="text-red-500">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    setImportFile(e.target.files[0] || null);
                    setImportErrors([]);
                    setImportSuccess(0);
                  }}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-green-50 file:text-green-700 file:font-medium hover:file:bg-green-100 border border-gray-300 rounded-lg p-1"
                />
                {importFile && (
                  <p className="text-xs text-gray-500 mt-1">Selected: {importFile.name}</p>
                )}
              </div>

              {/* Success */}
              {importSuccess > 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  <CheckCircle size={16} />
                  {importSuccess} row{importSuccess > 1 ? "s" : ""} imported successfully.
                </div>
              )}

              {/* Errors */}
              {importErrors.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg max-h-36 overflow-y-auto">
                  <p className="text-sm font-medium text-red-700 flex items-center gap-1 mb-1">
                    <AlertCircle size={15} /> {importErrors.length} error{importErrors.length > 1 ? "s" : ""}
                  </p>
                  {importErrors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">{e}</p>
                  ))}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={closeImport}
                  className="flex-1 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={importLoading || !importFile || !selectedCompany}
                  className="flex-1 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {importLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Importing...
                    </>
                  ) : (
                    <><Upload size={15} /> Import</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceMaster;
